import React, { useEffect, useRef, useState } from 'react'

type Entry = IntersectionObserverEntry
type Entries = IntersectionObserverEntry[]

const sortEntries = (a: Entry, b: Entry) =>
  a.intersectionRatio - b.intersectionRatio

/**
 * Intersection observer for section items
 *
 * @see https://developer.mozilla.org/en-US/docs/Web/API/Intersection_Observer_API
 */

type Props = {
  root: React.RefObject<HTMLDivElement>
  selector: string
}

/**
 * Options
 *
 * @see https://developer.mozilla.org/en-US/docs/Web/API/IntersectionObserver#instance_properties
 */
const options = {
  root: null, // observe document
  rootMargin: '-150px 0px 0px 0px',
  threshold: [0, 0.25, 0.5, 0.75, 1],
}

export const useItemsObserver = ({ root, selector }: Props) => {
  const observer = useRef<IntersectionObserver>()

  // initial observer callback is special
  const initial = useRef(true)

  // active items reference for access inside useEffect
  const items = useRef<Entries>([])

  // active items state
  const [activeItems, setActiveItems] = useState<Entries>([])

  // take last active item if any
  const activeItem = activeItems[activeItems.length - 1]

  // use it's data id
  const activeItemId = activeItem?.target.getAttribute('data-id')

  // use effect runs only one time at render because of empty deps []
  useEffect(() => {
    if (!root.current) return

    // dont run on mobile
    if (window.innerWidth < 768) return

    // create observer
    observer.current = new IntersectionObserver((entries) => {
      //
      // initial call, store all active entries
      //
      if (initial.current) {
        items.current = entries.filter((e) => e.intersectionRatio === 1)
        if (items.current.length === 0) {
          items.current = entries.filter((e) => e.isIntersecting)
        }
        items.current.sort(sortEntries)
        setActiveItems([...items.current])
        initial.current = false
        return
      }
      //
      // normal call add, update or remove entries
      //
      entries.forEach((entry) => {
        const current = items.current
        const { target, isIntersecting } = entry
        const index = current.findIndex((e) => e.target === target)
        if (isIntersecting) {
          if (index === -1) {
            items.current = [...current, entry] // add
          } else {
            items.current[index] = entry // update
          }
        } else {
          if (index > -1) {
            items.current = [
              ...current.slice(0, index),
              ...current.slice(index + 1),
            ] // remove
          }
        }
        // sort entries so we always end highest intersectionRatio last
        items.current.sort(sortEntries)
        setActiveItems([...items.current])
      })
    }, options)

    // observe all items
    const elements = root.current.querySelectorAll(selector)
    for (let i = 0; i < elements.length; i++) {
      observer.current.observe(elements[i])
    }

    // remove observer on unmount
    return () => {
      if (observer.current) {
        observer.current.disconnect()
      }
    }
  }, [])

  return activeItemId
}
